import React, { useState } from 'react';
import { Facebook, Linkedin, Twitter, Youtube, Instagram, MapPin, Mail, ArrowRight, Send, Loader2 } from 'lucide-react';

const Footer = ({ onNewsletter, onContact }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    const ok = onNewsletter ? await onNewsletter(email) : true;
    setLoading(false);
    if (ok) setEmail('');
  };

  return (
    <footer className="relative bg-[#1a2612] text-white overflow-hidden">
      {/* Decorative energy waves */}
      <div className="absolute inset-x-0 top-0 h-32 opacity-30 pointer-events-none">
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="w-full h-full">
          <path d="M0,50 Q360,10 720,50 T1440,50" stroke="#8ec045" strokeWidth="1" fill="none" opacity="0.4" />
          <path d="M0,60 Q360,20 720,60 T1440,60" stroke="#a3d859" strokeWidth="1" fill="none" opacity="0.3" />
          <path d="M0,70 Q360,30 720,70 T1440,70" stroke="#8ec045" strokeWidth="1" fill="none" opacity="0.2" />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-4 lg:px-8 pt-20 pb-10">
        {/* Newsletter */}
        <div className="mb-14 grid md:grid-cols-2 gap-6 md:gap-10 items-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 md:p-8">
          <div>
            <span className="text-[#a3d859] text-[11px] font-bold tracking-[4px]">STAY CONNECTED</span>
            <h3 className="text-2xl md:text-3xl font-bold mt-2 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Get Raynav Green updates in your inbox
            </h3>
          </div>
          <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/25 focus:border-[#a3d859] focus:bg-white/15 outline-none px-4 py-3 text-sm text-white placeholder:text-white/50 transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 bg-[#8ec045] hover:bg-[#a3d859] text-[#1a2612] font-bold text-[12px] tracking-widest px-6 py-3 disabled:opacity-70 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              SUBSCRIBE
            </button>
          </form>
        </div>

        {/* Top */}
        <div className="grid lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#a3d859] to-[#5a8c26] flex items-center justify-center shadow-lg">
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
                  <path d="M12 22V12" />
                  <path d="M3 7l9 5 9-5" />
                </svg>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-3xl font-bold text-white">Raynav</span>
                <span className="text-[11px] text-[#8ec045] font-bold tracking-[4px] mt-1">GREEN</span>
              </div>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold leading-snug mb-6 mt-6" style={{fontFamily:"'Playfair Display', serif"}}>
              Powering India with Green Energy Since 2010
            </h3>
            <div className="flex gap-3">
              {[Facebook, Linkedin, Twitter, Youtube, Instagram].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center hover:bg-[#8ec045] hover:border-[#8ec045] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-9 h-9 rounded-full bg-[#8ec045]/20 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[#a3d859]" />
              </div>
              <div>
                <div className="text-[11px] tracking-widest text-[#a3d859] mb-1">ADDRESS</div>
                <p className="text-[13px] text-white/75 leading-relaxed">
                  Raynav Green Private Limited<br />
                  LF 1 Plot 1A, Megha Heights<br />
                  Bairagarh Chichli, Kolar Road<br />
                  Bhopal, Madhya Pradesh 462042
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#8ec045]/20 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[#a3d859]" />
              </div>
              <div>
                <div className="text-[11px] tracking-widest text-[#a3d859] mb-1">CONTACT</div>
                <button
                  onClick={onContact}
                  className="inline-flex items-center gap-2 text-[13px] text-white hover:text-[#a3d859] transition-colors"
                >
                  Get in Touch <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-widest text-[#a3d859] mb-4">QUICK LINKS</div>
            <ul className="space-y-2 text-[13px]">
              {['Discover Raynav', 'Solar Projects', 'Wind Energy', 'Careers', 'Sustainability', 'Media Room'].map((l) => (
                <li key={l}>
                  <a href="#" className="text-white/75 hover:text-[#a3d859] transition-colors inline-flex items-center gap-2 group">
                    <span className="w-1 h-1 rounded-full bg-[#8ec045] group-hover:w-4 transition-all" />
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-white/50">
            © {new Date().getFullYear()} Raynav Green Private Limited. All rights reserved.
          </p>
          <div className="flex gap-6 text-[12px] text-white/50">
            <a href="#" className="hover:text-white/90">Privacy Policy</a>
            <a href="#" className="hover:text-white/90">Terms of Use</a>
            <a href="#" className="hover:text-white/90">Cookie Policy</a>
          </div>
          <div className="flex items-center gap-2 opacity-70">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#a3d859] to-[#5a8c26] flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
              </svg>
            </div>
            <span className="text-white/60 text-[11px] font-semibold tracking-widest">RAYNAV GREEN</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
